/* @flow */
import * as React from 'react';
import { css, names } from 'linaria';

import AppStoreBadges from '../../components/AppStoreBadges';
import Colors from '../../constants/Colors';
import {
  BodyText,
  Grid,
  GridCell,
  NumberedCircleBadge,
  Spacer,
  Title,
} from '../../components/core';
import DownloadLinkForm from '../../components/DownloadLinkForm';
import MediaQueries from '../../constants/MediaQueries';
import PhoneFrame from '../../components/PhoneFrame';
import Responsive from '../../constants/Responsive';
import withBreakpoint from '../../components/withBreakpoint';
import { prefetchImage } from '../../../utils';

const StepToScreenshot = {
  '1': {
    src: require('../../../assets/images/screenshots/map.png'),
    src2x: require('../../../assets/images/screenshots/map_2x.png'),
  },
  '2': {
    src: require('../../../assets/images/screenshots/vehicle-time.png'),
    src2x: require('../../../assets/images/screenshots/vehicle-time_2x.png'),
  },
  '3': {
    src: require('../../../assets/images/screenshots/photo-description.png'),
    src2x: require('../../../assets/images/screenshots/photo-description_2x.png'),
  },
};

function StepBlock({
  step,
  title,
  active,
  onSelect,
}: {
  step: number,
  title: string,
  active: boolean,
  onSelect: Function,
}) {
  return (
    <GridCell size={Responsive('size12of12', 'size4of12')}>
      <a
        href="#"
        onMouseOver={() => onSelect(step)}
        onClick={e => {
          onSelect(step);
          e.preventDefault();
        }}
        className={names(Styles.Step, active && Styles.StepActive)}>
        <NumberedCircleBadge>{step}</NumberedCircleBadge>
        <Title
          size="18"
          align={Responsive('left', 'center')}
          weight="bold"
          className={Styles.StepTitle}>
          {title}
        </Title>
      </a>
    </GridCell>
  );
}

class RequestInAppBlock extends React.Component<
  {
    title: string,
    breakpoint: string,
  },
  { activeStep: number }
> {
  state = { activeStep: 1 };

  static defaultProps = {
    title: 'Request in the app',
  };

  componentDidMount() {
    let retinaMediaQuery = window.matchMedia(MediaQueries.js(MediaQueries.Retina));
    // $FlowAssert
    Object.values(StepToScreenshot).forEach(({ src, src2x }) => {
      if (retinaMediaQuery.matches) {
        prefetchImage(src2x);
      } else {
        prefetchImage(src);
      }
    });
  }

  _onSelectStep = step => {
    this.setState({
      activeStep: step,
    });
  };

  render() {
    let { title, breakpoint } = this.props;
    let { activeStep } = this.state;

    return (
      <Grid vAlign="middle">
        {breakpoint !== 'small' && (
          <GridCell size={Responsive('size12of12', 'size5of12')} className={Styles.PhoneContent}>
            <PhoneFrame
              src={StepToScreenshot[activeStep].src}
              src2x={StepToScreenshot[activeStep].src2x}
            />
          </GridCell>
        )}
        <GridCell size={Responsive('size12of12', 'size7of12')} className={Styles.HowToContent}>
          <Title size="32" align="center" weight="bold">
            {title}
          </Title>
          <Spacer size={48} />
          <Grid vAlign="middle" hAlign="center">
            <StepBlock
              step={1}
              active={activeStep === 1}
              title="Set pickup & destination"
              onSelect={this._onSelectStep}
            />
            <StepBlock
              step={2}
              active={activeStep === 2}
              title="Choose vehicle & time"
              onSelect={this._onSelectStep}
            />
            <StepBlock
              step={3}
              active={activeStep === 3}
              title="Tell us what you're moving"
              onSelect={this._onSelectStep}
            />
          </Grid>
          {breakpoint !== 'small' ? (
            <Spacer size={100} color={Colors.Divider} />
          ) : (
            <div className={Styles.PhonesContainer}>
              <PhoneFrame
                src={require('../../../assets/images/screenshots/vehicle-time.png')}
                src2x={require('../../../assets/images/screenshots/vehicle-time_2x.png')}
                size="small"
                className={Styles.PhoneFrameLeft}
              />
              <PhoneFrame
                src={require('../../../assets/images/screenshots/map.png')}
                src2x={require('../../../assets/images/screenshots/map_2x.png')}
                screenshot="http://forums.imore.com/attachments/iphone-apps-games/57821d1401835287t-ios-8-screenshots-imageuploadedbytapatalk1401835283.252949.jpg"
                size="small"
                className={Styles.PhoneFrameRight}
              />
            </div>
          )}
          <Title size="22" align="center" weight="bold">
            {"Don't have the app?"}
          </Title>
          {breakpoint !== 'small' ? (
            <BodyText size="16" align="center">
              Enter your phone number and we’ll send you a link to download the app.
            </BodyText>
          ) : (
            <BodyText size="16" align="center">
              Download it for free on the App Store and get your Lugg started!
            </BodyText>
          )}
          {breakpoint !== 'small' ? <DownloadLinkForm /> : <AppStoreBadges />}
        </GridCell>
      </Grid>
    );
  }
}

const Styles = {
  PhoneContent: css`
    display: flex;
    justify-content: flex-end;
  `,
  HowToContent: css`
    ${MediaQueries.Large} {
      padding-left: 100px;
    }
  `,
  Step: css`
    align-items: center;
    background-color: transparent;
    border: 0;
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    margin-bottom: 24px;
    padding: 0;
    transition: opacity 0.2s;
    ${MediaQueries.Medium} {
      flex-direction: column;
      justify-content: space-between;
      margin-bottom: 0;
      opacity: 0.3;
      &:hover {
        opacity: 1;
      }
    }
  `,
  StepActive: css`
    ${MediaQueries.Medium} {
      opacity: 1;
    }
  `,
  StepTitle: css`
    margin-left: 12px;
    ${MediaQueries.Medium} {
      margin-top: 10px;
      margin-left: 0;
      max-width: 140px;
    }
  `,
  FormContainer: css`
    display: flex;
    margin-top: 30px;
    padding-left: 20px;
    ${MediaQueries.Large} {
      padding-left: 0;
    }
  `,
  PhonesContainer: css`
    width: 100%;
    height: 200px;
    overflow: hidden;
    border-bottom: 1px solid ${Colors.opacity(Colors.Black, 0.05)};
    position: relative;
    margin-bottom: 24px;
  `,
  PhoneFrameLeft: css`
    position: absolute;
    top: 10px;
    left: 50%;
    margin-left: -20px;
  `,
  PhoneFrameRight: css`
    position: absolute;
    top: 0;
    left: 50%;
    margin-left: -140px;
  `,
  TextInput: css`
    border-radius: 5px 0 0 5px;
    flex: 1;
  `,
  Button: css`
    border-radius: 0 5px 5px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
};

export default withBreakpoint(RequestInAppBlock);



// WEBPACK FOOTER //
// ./src/ui/pages/components/RequestInAppBlock.js